import type { CustomCellEditorProps } from "ag-grid-react";
import { type FC } from "react";
import { SymbolIcon } from "widgets";

import { ENTITY_TYPE, nonBreakingSpace } from "@/settings/settings.ts";

import { type IAgGridCellProps } from "../../cell-base-interfaces.js";
import ListCellContent, { type ListCellItem } from "./ListCellContent.js";
import { TableCell } from "./TableCell.js";

const OptimisticBooleanCell: FC<IAgGridCellProps | CustomCellEditorProps> = (props) => {
  return <BooleanCell {...(props as IAgGridCellProps)} highlightDisabled={false} />;
};

const PessimisticBooleanCell: FC<IAgGridCellProps> = (props) => {
  return <BooleanCell {...props} highlightDisabled={true} />;
};

const BooleanCell: FC<IAgGridCellProps & { highlightDisabled: boolean }> = (params) => {
  const { value, colDef, context, highlightDisabled } = params;
  if (value == null) {
    return null;
  }

  // In case a background color is applied (column-props-factory), a test color is also returned and should be applied to the icons too
  const color = typeof colDef?.cellStyle === "function" ? (colDef.cellStyle(params)?.color ?? undefined) : undefined;

  // Instead of a checkmark, we use the lock as symbol for "sample locked" cells.
  // This could be extended in the future for other locked entities.
  const isSampleLockedCell = params.data.groupType === ENTITY_TYPE.SAMPLE && params.colDef.field === "locked";

  const multiValues: ListCellItem[] = Array.isArray(value) ? value : [value];
  const items: ListCellItem[] = multiValues.map((booleanValue) => ({
    value:
      booleanValue == undefined ? (
        nonBreakingSpace
      ) : booleanValue ? (
        <SymbolIcon
          icon={isSampleLockedCell ? "lock" : "check"}
          style={{ color: color ?? (highlightDisabled ? "#DDDDDD" : "#2988CC") }}
          data-testid={"yes"}
        />
      ) : isSampleLockedCell ? null : (
        <SymbolIcon
          icon={"close"}
          style={{ color: color ?? (highlightDisabled ? "#e85777" : "#DDDDDD") }}
          data-testid={"no"}
        />
      ),
  }));

  return (
    <TableCell style={{ textAlign: "center" }}>
      <ListCellContent items={items} colDef={colDef} context={context} />
    </TableCell>
  );
};

export { OptimisticBooleanCell, PessimisticBooleanCell };
